import dayjs from 'dayjs';

import { BlogPostStatus } from '../../../api/blog';
import type { BlogCategory, BlogPost, BlogPostSummary } from '../../../api/blog';

/**
 * Dữ liệu cho phần xem trước giao diện website — gom mọi nguồn (danh sách, chi tiết, bài đang soạn) về một
 * dạng, để giao diện xem trước không phải biết dữ liệu đến từ đâu.
 */

/** Tình trạng bài trong bản xem trước: khách chỉ thấy bài "published". */
export type PreviewStatus = 'published' | 'scheduled' | 'draft';

/** Một bài trên thẻ danh sách. */
export interface PreviewPost {
  readonly id: string;
  readonly slug: string;
  readonly title: string;
  readonly excerpt: string;
  readonly coverImageUrl: string | null;
  readonly coverImageAlt: string;
  readonly categoryName: string | null;
  readonly categorySlug: string | null;
  readonly authorName: string | null;
  /** Ngày hiện trên bài, dạng ISO; rỗng khi bài chưa đăng lần nào. */
  readonly date: string | null;
  readonly status: PreviewStatus;
}

/** Một bài đầy đủ, cho trang chi tiết. */
export interface PreviewArticle extends PreviewPost {
  readonly contentHtml: string;
}

/** Một danh mục ở cột bên, kèm số bài. */
export interface PreviewCategory {
  readonly name: string;
  readonly slug: string;
  readonly count: number;
}

/** Tốc độ đọc trung bình — cùng con số máy chủ dùng. */
const wordsPerMinute = 200;

export function statusOf(post: Pick<BlogPostSummary, 'status' | 'isScheduled'>): PreviewStatus {
  if (post.status === BlogPostStatus.Draft) {
    return 'draft';
  }

  return post.isScheduled ? 'scheduled' : 'published';
}

function categoryOf(
  categoryId: string | null,
  categories: readonly BlogCategory[],
): { readonly name: string | null; readonly slug: string | null } {
  const category = categories.find((candidate) => candidate.id === categoryId);

  return { name: category?.name ?? null, slug: category?.slug ?? null };
}

export function fromSummary(post: BlogPostSummary, categories: readonly BlogCategory[]): PreviewPost {
  const category = categoryOf(post.categoryId, categories);

  return {
    id: post.id,
    slug: post.slug,
    title: post.title,
    excerpt: post.excerpt,
    coverImageUrl: post.coverImageUrl,
    coverImageAlt: post.title,
    categoryName: category.name,
    categorySlug: category.slug,
    authorName: post.authorName,
    date: post.publishedAt,
    status: statusOf(post),
  };
}

export function fromPost(post: BlogPost, categories: readonly BlogCategory[]): PreviewArticle {
  return {
    ...fromSummary(post, categories),
    coverImageAlt: post.coverImageAlt ?? post.title,
    contentHtml: post.contentHtml,
  };
}

/** Thời gian đọc ước tính, tối thiểu một phút. */
export function readingMinutes(html: string): number {
  const text = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html').body.textContent;
  const words = text.split(/\s+/).filter((word) => word !== '').length;

  return Math.max(1, Math.ceil(words / wordsPerMinute));
}

/** Ngày dạng 27/10/2026; bài chưa đăng thì hiện ngày hôm nay, như lúc bấm đăng ngay. */
export function formatPreviewDate(value: string | null): string {
  return (value === null ? dayjs() : dayjs(value)).format('DD/MM/YYYY');
}
